<template>
  <v-dialog
    v-model="dialog"
    max-width="500px"
    scrollable
  >
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center">
        <span>プロパティ管理</span>
        <v-btn
          icon
          @click="dialog = false"
        >
          <v-icon>{{ icons.mdiClose }}</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider />
      <v-card-text
        class="pa-0"
        style="max-height: 60vh;"
      >
        <v-list
          v-if="localFields.length > 0 || isAdding"
          dense
          class="py-0"
        >
          <div
            ref="sortableContainer"
            class="sortable-list"
          >
            <v-list-item
              v-for="field in localFields"
              :key="field.id"
              class="sortable-item"
              :data-id="field.id"
            >
              <!-- 編集モード -->
              <template v-if="editingId === field.id">
                <v-list-item-icon class="mr-2 my-auto">
                  <v-icon small>
                    {{ icons.mdiDrag }}
                  </v-icon>
                </v-list-item-icon>
                <v-list-item-content class="py-1">
                  <v-text-field
                    v-model="editName"
                    dense
                    hide-details
                    autofocus
                    class="mt-0 pt-0"
                    @keydown.enter="saveEdit(field)"
                    @keydown.esc="cancelEdit"
                  />
                </v-list-item-content>
                <v-list-item-action class="flex-row my-auto">
                  <v-btn
                    icon
                    small
                    color="primary"
                    :disabled="!editName"
                    @click="saveEdit(field)"
                  >
                    <v-icon small>
                      {{ icons.mdiCheck }}
                    </v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    small
                    @click="cancelEdit"
                  >
                    <v-icon small>
                      {{ icons.mdiClose }}
                    </v-icon>
                  </v-btn>
                </v-list-item-action>
              </template>

              <!-- 表示モード -->
              <template v-else>
                <v-list-item-icon class="mr-2 my-auto drag-handle">
                  <v-icon
                    small
                    style="cursor: grab;"
                  >
                    {{ icons.mdiDrag }}
                  </v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>{{ field.name }}</v-list-item-title>
                  <v-list-item-subtitle class="text-caption">
                    {{ fieldTypeLabel(field.type) }}
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action class="flex-row my-auto">
                  <v-btn
                    icon
                    small
                    @click="startEdit(field)"
                  >
                    <v-icon small>
                      {{ icons.mdiPencil }}
                    </v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    small
                    @click="$emit('delete', field)"
                  >
                    <v-icon
                      small
                      color="error"
                    >
                      {{ icons.mdiDelete }}
                    </v-icon>
                  </v-btn>
                </v-list-item-action>
              </template>
            </v-list-item>
          </div>

          <!-- 新規追加行 -->
          <v-list-item v-if="isAdding">
            <v-list-item-icon class="mr-2 my-auto">
              <v-icon small>
                {{ icons.mdiPlus }}
              </v-icon>
            </v-list-item-icon>
            <v-list-item-content class="py-1">
              <v-row
                no-gutters
                class="align-center"
              >
                <v-col>
                  <v-text-field
                    v-model="newName"
                    dense
                    hide-details
                    placeholder="プロパティ名"
                    autofocus
                    class="mt-0 pt-0"
                    @keydown.enter="saveNew"
                    @keydown.esc="cancelAdd"
                  />
                </v-col>
                <v-col
                  cols="auto"
                  class="ml-2"
                >
                  <v-select
                    v-model="newType"
                    :items="propertyTypes"
                    dense
                    hide-details
                    class="mt-0 pt-0"
                    style="max-width: 140px;"
                  />
                </v-col>
              </v-row>
            </v-list-item-content>
            <v-list-item-action class="flex-row my-auto">
              <v-btn
                icon
                small
                color="primary"
                :disabled="!newName"
                @click="saveNew"
              >
                <v-icon small>
                  {{ icons.mdiCheck }}
                </v-icon>
              </v-btn>
              <v-btn
                icon
                small
                @click="cancelAdd"
              >
                <v-icon small>
                  {{ icons.mdiClose }}
                </v-icon>
              </v-btn>
            </v-list-item-action>
          </v-list-item>
        </v-list>
        <div
          v-else
          class="pa-6 text-center grey--text"
        >
          プロパティがありません
        </div>
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <v-btn
          text
          color="primary"
          :disabled="isAdding"
          @click="startAdd"
        >
          <v-icon left>
            {{ icons.mdiPlus }}
          </v-icon>
          プロパティを追加
        </v-btn>
        <v-spacer />
        <v-btn
          text
          @click="dialog = false"
        >
          閉じる
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  computed, ref, watch, nextTick, onBeforeUnmount,
} from '@vue/composition-api'
import {
  mdiClose, mdiDrag, mdiPencil, mdiDelete, mdiPlus, mdiCheck,
} from '@mdi/js'
import Sortable from 'sortablejs'

export default {
  name: 'CustomerPropertyManagerDialog',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    customFields: {
      type: Array,
      default: () => [],
    },
  },
  setup(props, { emit }) {
    const dialog = computed({
      get: () => props.value,
      set: value => emit('input', value),
    })

    const localFields = ref([])
    const sortableContainer = ref(null)
    let sortableInstance = null

    // 編集状態
    const editingId = ref(null)
    const editName = ref('')

    // 新規追加状態
    const isAdding = ref(false)
    const newName = ref('')
    const newType = ref('text')

    const propertyTypes = [
      { text: 'テキスト', value: 'text' },
      { text: '数値', value: 'number' },
      { text: 'チェックボックス', value: 'boolean' },
    ]

    const fieldTypeLabel = type => {
      const map = {
        text: 'テキスト',
        number: '数値',
        boolean: 'チェックボックス',
      }

      return map[type] || type
    }

    const startEdit = field => {
      isAdding.value = false
      editingId.value = field.id
      editName.value = field.name
    }

    const saveEdit = field => {
      if (!editName.value) return
      emit('update', { id: field.id, name: editName.value })
      editingId.value = null
      editName.value = ''
    }

    const cancelEdit = () => {
      editingId.value = null
      editName.value = ''
    }

    const startAdd = () => {
      cancelEdit()
      isAdding.value = true
      newName.value = ''
      newType.value = 'text'
    }

    const saveNew = () => {
      if (!newName.value) return
      emit('add', { name: newName.value, type: newType.value })
      newName.value = ''
      newType.value = 'text'
      isAdding.value = false
    }

    const cancelAdd = () => {
      isAdding.value = false
      newName.value = ''
      newType.value = 'text'
    }

    const initSortable = () => {
      if (sortableInstance) {
        sortableInstance.destroy()
        sortableInstance = null
      }
      if (!sortableContainer.value) return

      sortableInstance = Sortable.create(sortableContainer.value, {
        animation: 150,
        handle: '.drag-handle',
        ghostClass: 'sortable-ghost',
        onEnd: () => {
          const items = sortableContainer.value.querySelectorAll('.sortable-item')
          const ids = Array.from(items).map(el => el.dataset.id)
          emit('reorder', ids)
        },
      })
    }

    watch(() => props.customFields, newVal => {
      localFields.value = [...newVal]
    }, { immediate: true, deep: true })

    watch(dialog, newVal => {
      if (newVal) {
        localFields.value = [...props.customFields]
        cancelEdit()
        cancelAdd()
        nextTick(() => {
          initSortable()
        })
      } else if (sortableInstance) {
        sortableInstance.destroy()
        sortableInstance = null
      }
    })

    onBeforeUnmount(() => {
      if (sortableInstance) {
        sortableInstance.destroy()
      }
    })

    return {
      dialog,
      localFields,
      sortableContainer,
      editingId,
      editName,
      isAdding,
      newName,
      newType,
      propertyTypes,
      fieldTypeLabel,
      startEdit,
      saveEdit,
      cancelEdit,
      startAdd,
      saveNew,
      cancelAdd,
      icons: {
        mdiClose,
        mdiDrag,
        mdiPencil,
        mdiDelete,
        mdiPlus,
        mdiCheck,
      },
    }
  },
}
</script>

<style scoped>
.sortable-ghost {
  opacity: 0.5;
  background: #e8eaf6;
}

.drag-handle:hover {
  background: rgba(0, 0, 0, 0.04);
  border-radius: 4px;
}
</style>
